import type { CaravanDb } from './db'
import { MediaRepository } from './mediaRepository'
import { LocalRepository } from './repository'
import { isDailyCandidateAdmissible } from '../../domain/dailyPhoto'
import type { LocalEntity } from '../../domain/sync'
import type { ProcessedImageBundle } from '../../services/imagePipeline'
import type { PreparedAlbumVideo } from '../../services/videoPipeline'
import type { LocalBlob } from './db'

export type PhotoPurpose = 'album' | 'daily_candidate'
export type PhotoInput = {
  tripId: string
  memberId: string
  displayName: string
  purpose: PhotoPurpose
  productDay: string
  caption: string
  createdAtDevice: string
  fileName: string
}
export type PhotoRecord = PhotoInput & {
  id: string
  lastMutationId: string
  syncState: LocalEntity['syncState']
  uploadState: 'pending' | 'uploading' | 'confirmed' | 'error'
  imagePath: string | null
  thumbnailPath: string | null
  receivedAtServer: string | null
}
export type AlbumVideoInput = Omit<PhotoInput, 'purpose'> & {
  durationSeconds: number
}
export type AlbumVideoRecord = AlbumVideoInput & {
  id: string
  lastMutationId: string
  syncState: LocalEntity['syncState']
  uploadState: 'pending' | 'confirmed'
  thumbnailAvailable: boolean
}
export type CommentRecord = { id: string; photoId: string; memberId: string; text: string; createdAtDevice: string }

function readString(payload: Record<string, unknown>, key: string): string {
  return typeof payload[key] === 'string' ? payload[key] : ''
}

async function stableLikeId(photoId: string, memberId: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`caravan-like:${photoId}:${memberId}`))).slice(0, 16)
  digest[6] = ((digest[6] ?? 0) & 0x0f) | 0x40
  digest[8] = ((digest[8] ?? 0) & 0x3f) | 0x80
  const hex = Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function photoFromEntity(entity: LocalEntity): PhotoRecord {
  return {
    id: entity.id,
    tripId: entity.tripId,
    memberId: readString(entity.payload, 'memberId'),
    displayName: readString(entity.payload, 'displayName'),
    purpose: entity.payload.purpose === 'daily_candidate' ? 'daily_candidate' : 'album',
    productDay: readString(entity.payload, 'productDay'),
    caption: readString(entity.payload, 'caption'),
    createdAtDevice: entity.createdAtDevice,
    fileName: readString(entity.payload, 'fileName'),
    lastMutationId: entity.lastMutationId,
    syncState: entity.syncState,
    uploadState: ['uploading', 'confirmed', 'error'].includes(String(entity.payload.uploadState))
      ? entity.payload.uploadState as PhotoRecord['uploadState']
      : 'pending',
    imagePath: readString(entity.payload, 'imagePath') || null,
    thumbnailPath: readString(entity.payload, 'thumbnailPath') || null,
    receivedAtServer: readString(entity.payload, 'receivedAtServer') || null,
  }
}

function videoFromEntity(entity: LocalEntity): AlbumVideoRecord {
  return {
    id: entity.id,
    tripId: entity.tripId,
    memberId: readString(entity.payload, 'memberId'),
    displayName: readString(entity.payload, 'displayName'),
    productDay: readString(entity.payload, 'productDay'),
    caption: readString(entity.payload, 'caption'),
    createdAtDevice: entity.createdAtDevice,
    fileName: readString(entity.payload, 'fileName'),
    durationSeconds: typeof entity.payload.durationSeconds === 'number' ? entity.payload.durationSeconds : 0,
    lastMutationId: entity.lastMutationId,
    syncState: entity.syncState,
    uploadState: entity.payload.uploadState === 'confirmed' ? 'confirmed' : 'pending',
    thumbnailAvailable: entity.payload.thumbnailAvailable === true,
  }
}

export class AlbumRepository {
  readonly #db: CaravanDb
  readonly #media: MediaRepository
  readonly #local: LocalRepository

  constructor(db: CaravanDb, media: MediaRepository) {
    this.#db = db
    this.#media = media
    this.#local = new LocalRepository(db)
  }

  async addPhoto(input: PhotoInput, bundle: ProcessedImageBundle): Promise<PhotoRecord> {
    if (input.purpose === 'daily_candidate' && !isDailyCandidateAdmissible(input.createdAtDevice, input.productDay)) {
      throw new Error('La candidature photo du jour doit être créée entre 02:00 et 22:00 au Maroc.')
    }
    const id = crypto.randomUUID()
    await this.#media.storeBundle(id, bundle, input.createdAtDevice)
    try {
      const entity = await this.#local.mutate({
        mutationId: crypto.randomUUID(),
        entityType: 'photo',
        entityId: id,
        tripId: input.tripId,
        operation: 'create',
        baseVersion: 0,
        payload: { ...input, createdAtDevice: undefined, imageMetadata: bundle.metadata, uploadState: 'pending' },
        createdAtDevice: input.createdAtDevice,
      })
      return photoFromEntity(entity)
    } catch (error) {
      await this.#media.deleteUncommittedBundle(id)
      throw error
    }
  }

  async listPhotos(tripId?: string): Promise<PhotoRecord[]> {
    const rows = await this.#db.entities.where('entityType').equals('photo')
      .filter((entity) => entity.deletedAt === null && (!tripId || entity.tripId === tripId))
      .toArray()
    return rows.map(photoFromEntity).sort((left, right) => right.createdAtDevice.localeCompare(left.createdAtDevice))
  }

  async addVideo(input: AlbumVideoInput, video: PreparedAlbumVideo): Promise<AlbumVideoRecord> {
    const id = crypto.randomUUID()
    const rows: LocalBlob[] = [{
      key: `${id}:original`, entityId: id, kind: 'original', bytes: await video.file.arrayBuffer(),
      mimeType: video.file.type, confirmedRemote: false, createdAt: input.createdAtDevice,
    }]
    if (video.thumbnail) rows.push({
      key: `${id}:thumbnail`, entityId: id, kind: 'thumbnail', bytes: await video.thumbnail.arrayBuffer(),
      mimeType: video.thumbnail.type, confirmedRemote: false, createdAt: input.createdAtDevice,
    })
    await this.#db.blobs.bulkPut(rows)
    try {
      const entity = await this.#local.mutate({
        mutationId: crypto.randomUUID(), entityType: 'album_video', entityId: id, tripId: input.tripId,
        operation: 'create', baseVersion: 0,
        payload: { ...input, createdAtDevice: undefined, uploadState: 'pending', thumbnailAvailable: Boolean(video.thumbnail) },
        createdAtDevice: input.createdAtDevice,
      })
      return videoFromEntity(entity)
    } catch (error) {
      await this.#db.blobs.where('entityId').equals(id).delete()
      throw error
    }
  }

  async listVideos(tripId?: string): Promise<AlbumVideoRecord[]> {
    const rows = await this.#db.entities.where('entityType').equals('album_video')
      .filter((entity) => entity.deletedAt === null && (!tripId || entity.tripId === tripId))
      .toArray()
    return rows.map(videoFromEntity).sort((left, right) => right.createdAtDevice.localeCompare(left.createdAtDevice))
  }

  async getVideo(videoId: string, kind: 'original' | 'thumbnail' = 'original'): Promise<Blob | null> {
    const row = await this.#db.blobs.get(`${videoId}:${kind}`)
    return row ? new Blob([row.bytes], { type: row.mimeType }) : null
  }

  getMedia(photoId: string) {
    return this.#media.getBundle(photoId)
  }

  async deletePhoto(photoId: string, memberId: string): Promise<void> {
    await this.#deleteOwnedMedia('photo', photoId, memberId)
  }

  async deleteVideo(videoId: string, memberId: string): Promise<void> {
    await this.#deleteOwnedMedia('album_video', videoId, memberId)
  }

  async #deleteOwnedMedia(entityType: 'photo' | 'album_video', mediaId: string, memberId: string): Promise<void> {
    const entity = await this.#db.entities.get(`${entityType}:${mediaId}`)
    if (entity?.deletedAt !== null) return
    if (readString(entity.payload, 'memberId') !== memberId) {
      throw new Error('Seul l’auteur de ce souvenir peut le supprimer.')
    }
    await this.#local.mutate({
      mutationId: crypto.randomUUID(), entityType, entityId: mediaId, tripId: entity.tripId,
      operation: 'delete', baseVersion: entity.version, payload: entity.payload,
      createdAtDevice: new Date().toISOString(),
    })
    await this.#db.blobs.where('entityId').equals(mediaId).delete()
  }

  async addComment(photoId: string, tripId: string, memberId: string, text: string): Promise<CommentRecord> {
    const clean = text.trim()
    if (!clean) throw new Error('Le commentaire ne peut pas être vide.')
    const id = crypto.randomUUID()
    const createdAtDevice = new Date().toISOString()
    const entity = await this.#local.mutate({
      mutationId: crypto.randomUUID(), entityType: 'comment', entityId: id, tripId,
      operation: 'create', baseVersion: 0, payload: { photoId, memberId, text: clean }, createdAtDevice,
    })
    return { id: entity.id, photoId, memberId, text: clean, createdAtDevice }
  }

  async listComments(photoId: string): Promise<CommentRecord[]> {
    const rows = await this.#db.entities.where('entityType').equals('comment')
      .filter((entity) => entity.deletedAt === null && entity.payload.photoId === photoId)
      .sortBy('createdAtDevice')
    return rows.map((entity) => ({
      id: entity.id,
      photoId,
      memberId: readString(entity.payload, 'memberId'),
      text: readString(entity.payload, 'text'),
      createdAtDevice: entity.createdAtDevice,
    }))
  }

  async setLike(photoId: string, tripId: string, memberId: string, selected: boolean, mutationId = crypto.randomUUID()): Promise<void> {
    const likes = await this.#db.entities.where('entityType').equals('like')
      .filter((entity) => entity.payload.photoId === photoId && entity.payload.memberId === memberId)
      .toArray()
    const existing = likes[0]
    await this.#local.mutate({
      mutationId, entityType: 'like', entityId: existing?.id ?? await stableLikeId(photoId, memberId), tripId,
      operation: 'upsert', baseVersion: existing?.version ?? 0,
      payload: { photoId, memberId, selected }, createdAtDevice: new Date().toISOString(),
    })
  }

  async getLikeSummary(photoId: string, memberId: string): Promise<{ count: number; selected: boolean }> {
    const rows = await this.#db.entities.where('entityType').equals('like')
      .filter((entity) => entity.deletedAt === null && entity.payload.photoId === photoId && entity.payload.selected === true)
      .toArray()
    return { count: new Set(rows.map((row) => readString(row.payload, 'memberId'))).size, selected: rows.some((row) => row.payload.memberId === memberId) }
  }
}
